import React, { useContext, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MOBILE_MEASURE_TOOLS } from "../../../../constants/Constants";
import MobileViewerContext from "../../../../context/studyviewer/MobileViewerContext";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import ThumbnailContext from "../../../../context/studyviewer/ThumbnailContext";
import GetToolSVG from "../Tools/ToolComponents/GetToolSVG";
import DeleteIcon from "../../../../components/SVGComponents/DeleteIcon";

// Anchored dropdown that opens UPWARD above the "Measure" toolbar button — positioned
// exactly like the Rotate dropdown next to it (position:fixed, coords computed from the
// button's bounding rect). The earlier CSS-class approach fought the
// `.dropdow-bottom-position { bottom:48px !important }` rule and mis-positioned the menu.
const MobileMeasureSheet = ({ anchorRef }) => {
  const { setShowMeasureSheet } = useContext(MobileViewerContext);
  const {
    activeTool,
    setActiveTool,
    setPreviousActiveTool,
    isMPRActive,
    activeMPRTool,
    setActiveMPRTool,
    setPreviousActiveMPRTool,
    removeSelectedMeasurement,
    removeAllMeasurements,
    handleDefaultToolChange,
  } = useContext(ViewportContext);
  const { activeModality } = useContext(ThumbnailContext);
  const { t } = useTranslation();

  // Bottom toolbar -> dropdown opens upward. Anchor the menu's bottom edge just above the
  // Measure button and align its left to the button, computed from the button's rect
  // (same technique as MobileRotateMenu).
  const [menuPos, setMenuPos] = useState({ bottom: 0, left: 0 });
  useLayoutEffect(() => {
    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setMenuPos({
        bottom: window.innerHeight - rect.top + 6,
        left: rect.left,
      });
    }
  }, [anchorRef]);

  // In MPR, measurement tools must activate via the MPR tool path so they apply
  // to the MPR (volume) viewport; the stack path (setActiveTool) only drives the
  // 2D stack viewport and would do nothing in MPR.
  const handleSelect = (tool) => {
    // re-clicking the already-active measure tool deselects it (reverts
    // to the default tool), for every tool in the Measure menu — same toggle
    // behaviour as desktop. OLD: always activated the tapped tool.
    const isAlreadyActive = isMPRActive
      ? activeMPRTool === tool.key
      : activeTool === tool.key;
    if (isAlreadyActive) {
      if (isMPRActive) {
        setPreviousActiveMPRTool(activeMPRTool);
      } else {
        setPreviousActiveTool(activeTool);
      }
      handleDefaultToolChange();
      setShowMeasureSheet(false);
      return;
    }
    if (isMPRActive) {
      setPreviousActiveMPRTool(activeMPRTool);
      setActiveMPRTool(tool.key);
    } else {
      setPreviousActiveTool(activeTool);
      setActiveTool(tool.key);
    }
    setShowMeasureSheet(false);
  };

  return (
    <div
      className="viewer-tool-more-dropdown viewer-tool-more-dropdown-height"
      style={{
        position: "fixed",
        bottom: menuPos.bottom,
        left: menuPos.left,
        zIndex: 9999,
        minWidth: "170px",
        display: "flex",
        flexDirection: "column",
        borderRadius: "5px",
        paddingTop: "6px",
        paddingBottom: "6px",
        boxShadow:
          "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
      }}
    >
      {MOBILE_MEASURE_TOOLS.map((tool, index) => {
        if (tool.NotFor.includes(activeModality)) {
          return null;
        }
        const isActive = isMPRActive
          ? activeMPRTool === tool.key
          : activeTool === tool.key;
        return (
          <div
            key={index}
            onClick={() => handleSelect(tool)}
            className="stack-scroll-dropdown-elements"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "8px 14px",
              cursor: "pointer",
              background: isActive ? "rgba(255, 184, 1, 0.12)" : "transparent",
            }}
          >
            <span
              style={{
                display: "flex",
                width: "20px",
                height: "20px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* `hovered` doubles as the active-highlight flag for the icon */}
              <GetToolSVG toolName={tool.key} hovered={isActive} />
            </span>
            <span
              style={{
                fontSize: "13px",
                whiteSpace: "nowrap",
                color: "var(--mobile-color-txt)",
              }}
            >
              {t(tool.name)}
            </span>
          </div>
        );
      })}

      {/*  Delete Selected / Delete All — same actions as the desktop
          ToolDropDown "more" menu. Delete Selected removes the currently selected annotation;
          Delete All removes every (non-protected) annotation. (Distinct from "Reset", which only
          resets the viewport transform — zoom/pan/window-level/rotation — and keeps annotations.) */}
      <div style={{ borderTop: "1px solid #3B3A39", margin: "4px 0" }} />

      <div
        onClick={() => {
          removeSelectedMeasurement();
          setShowMeasureSheet(false);
        }}
        className="stack-scroll-dropdown-elements"
        style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 14px", cursor: "pointer" }}
      >
        <span style={{ display: "flex", width: "20px", height: "20px", alignItems: "center", justifyContent: "center" }}>
          <DeleteIcon color="var(--mobile-color-txt)" />
        </span>
        <span style={{ fontSize: "13px", whiteSpace: "nowrap", color: "var(--mobile-color-txt)" }}>
          {t("viewer.tools.delete-selected-measurement", "Delete Selected")}
        </span>
      </div>

      <div
        onClick={() => {
          removeAllMeasurements();
          setShowMeasureSheet(false);
        }}
        className="stack-scroll-dropdown-elements"
        style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 14px", cursor: "pointer" }}
      >
        <span style={{ display: "flex", width: "20px", height: "20px", alignItems: "center", justifyContent: "center" }}>
          <DeleteIcon color="var(--mobile-color-txt)" />
        </span>
        <span style={{ fontSize: "13px", whiteSpace: "nowrap", color: "var(--mobile-color-txt)" }}>
          {t("viewer.tools.delete-all-measurements", "Delete All")}
        </span>
      </div>
    </div>
  );
};

export default MobileMeasureSheet;